import { FC, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import {
  CssBaseline,
  Stack,
  StackProps,
  styled,
  useTheme,
} from '@mui/material';
import { useChannel } from 'ably/react';

import { NotificationPanel } from '@/components';
import CLIWidget from '@/components/CLIWidget/CLIWidget';
import { OfficeChatPanel } from '@/components/OfficeChat';
import { SexOffenderDueNotification } from '@/components/SexOffenderDueNotification/SexOffenderDueNotification';
import { Toaster } from '@/components/TailwindComponents/base';
import { MainPageLayout } from '@/components/TailwindComponents/components/Layouts/MainPageLayout';
import { useAuthContext } from '@/hooks';
import { useRefetchUserOnNavigation } from '@/hooks/useRefetchUserOnNavigation';
import useShowCareLink from '@/hooks/useShowCareLink';
import useShowIncomingCalls from '@/hooks/useShowIncomingCalls';
import { IUser, Role } from '@/models';
import { isAgencyAdmin, isDispatcherRole } from '@/services';
import {
  useAgencyStore,
  useCFSNavigationStore,
  useOfficeChatStore,
  useUserStore,
} from '@/store';
import { sizes } from '@/theme/variables';
import { isOpenedInNewTab } from '@/utils';

import { CareLink } from './components/CareLink';
import { IncomingCall } from './components/IncomingCall';
import MainSideBar from './components/SideBar/MainSideBar';

interface MainLayoutRootProps extends StackProps {
  hasPageLayoutPadding?: boolean;
}

/**
 * @note hasPageLayoutPadding prop is used to align the Fire RMS page layout design.
 */
const MainLayoutRoot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasPageLayoutPadding',
})<MainLayoutRootProps>(({ theme, hasPageLayoutPadding }) => ({
  width: '100vw',
  height: '100vh',
  flexDirection: 'row',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,

  '& main': {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100vh',
    overflow: 'hidden',
    contain: 'strict',
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  },

  '& .main': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100vh',
    padding: hasPageLayoutPadding ? '0px 20px 15px' : 0,
    overflow: 'hidden',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      paddingLeft: sizes.mainPadding.md,
      paddingRight: sizes.mainPadding.md,
      paddingBottom: sizes.mainPadding.md,
    },
  },
  '& .fullscreen': {
    padding: 0,
  },
}));

type MainLayoutProps = {
  isFullScreen?: boolean;
};

export const MainLayout: FC<MainLayoutProps> = ({ isFullScreen = false }) => {
  const theme = useTheme();
  const { user, account, userAgencyId } = useUserStore();
  const { currentAgency, fetchAgency } = useAgencyStore();
  const { setHasAccess, setCurrentUserId, loadUnreadCount } =
    useOfficeChatStore();
  const { accessToken, logout } = useAuthContext();
  const showIncomingCalls = useShowIncomingCalls(user);
  useRefetchUserOnNavigation();

  const location = useLocation();
  // get current path
  const currentPath = location.pathname;
  const isOpenedInNewTabMode = isOpenedInNewTab(
    new URLSearchParams(location.search),
  );
  const isFireRMS = currentPath.includes('fire-cases');
  const hasPageLayoutPadding = !isFireRMS;
  const isKeyboardShortcutsPage = currentPath.includes('keyboard-shortcuts');

  const isEnabledCLI = account?.settings?.features?.isEnabledCLI;

  const isCLIRole =
    isAgencyAdmin(user as IUser) || isDispatcherRole(user as IUser);

  const isEnabledNursingHome = useShowCareLink(account);

  try {
    useChannel(`account:${account?._id}:force-password-reset`, ({ data }) => {
      if (data.userId === user?._id) {
        logout();
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('e', e);
  }

  // Fetch current agency if not loaded
  useEffect(() => {
    if (userAgencyId && !currentAgency) {
      fetchAgency(userAgencyId);
    }
  }, [userAgencyId, currentAgency, fetchAgency]);

  // Clear CFS cache when navigating away from CFS area
  useEffect(() => {
    const isCFSArea =
      currentPath.startsWith('/cfs') || currentPath.startsWith('/cfs-active');
    if (!isCFSArea) {
      useCFSNavigationStore.getState().clearCFSCache();
    }
  }, [currentPath]);

  useEffect(() => {
    setCurrentUserId(user?._id || null);
  }, [user?._id, setCurrentUserId]);

  // Set Office Chat access based on user role and agency settings
  useEffect(() => {
    const hasOfficeChatRole = user?.roles?.some((r) => {
      if (r.role !== Role.ACCESS_OFFICE_CHAT) return false;
      const agencyId = typeof r.agency === 'string' ? r.agency : r.agency?._id;
      return agencyId === currentAgency?._id;
    });
    const agencyHasOfficeChat = currentAgency?.isOfficeChatEnabled;

    const hasOfficeChatAdminRole = user?.roles?.some((r) => {
      if (r.role !== Role.OFFICE_CHAT_ADMIN) return false;
      const agencyId = typeof r.agency === 'string' ? r.agency : r.agency?._id;
      return agencyId === currentAgency?._id;
    });

    // Access is enabled if the role as set and the agency feature setting is set
    const hasOfficeChatAccess = !!hasOfficeChatRole && !!agencyHasOfficeChat;
    // Chat admin is enabled when access is true and the user has the chat admin role
    const isOfficeChatAdmin = hasOfficeChatAccess && hasOfficeChatAdminRole;

    setHasAccess(hasOfficeChatAccess, !!isOfficeChatAdmin);
    if (hasOfficeChatAccess) {
      loadUnreadCount();
    }
  }, [user, currentAgency, setHasAccess, loadUnreadCount]);

  useEffect(() => {
    // Check token expiration every 5 minutes.
    const checkTokenExpirationInterval = setInterval(() => {
      if (!accessToken) return;
      const decodedJwt = JSON.parse(atob(accessToken?.split('.')[1]));

      if (decodedJwt.exp * 1000 < Date.now()) {
        logout();
      }
    }, 300 * 1000);

    return () => {
      clearInterval(checkTokenExpirationInterval);
    };
  }, []);

  return (
    <MainLayoutRoot theme={theme} hasPageLayoutPadding={hasPageLayoutPadding}>
      <CssBaseline />

      {/* Sidebar */}
      {!isOpenedInNewTabMode && <MainSideBar />}

      {/* Page view */}
      {isFireRMS ? (
        <>
          <Toaster />
          <MainPageLayout>
            <Outlet />
          </MainPageLayout>
        </>
      ) : (
        <main>
          <div className={isFullScreen ? 'main fullscreen' : 'main'}>
            <Outlet />
            {isEnabledNursingHome && !isFullScreen && <CareLink />}
            {showIncomingCalls && !isFullScreen && !isKeyboardShortcutsPage && (
              <IncomingCall />
            )}
          </div>
        </main>
      )}

      {/* Global Notification Panel */}
      <NotificationPanel />

      {/* Sex Offender Due Notification */}
      <SexOffenderDueNotification />

      {/* Office Chat Panel */}
      <OfficeChatPanel />

      {/* reverse this after feature is implemented */}
      {!isEnabledCLI && isCLIRole && <CLIWidget />}
    </MainLayoutRoot>
  );
};
